import {EmptyStatus, Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {RecordAuditTask, RecordAuditResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {RecordAuditProgressSegments} from './RecordAuditProgressSegments';
import {RecordAuditScoreFilter} from './RecordAuditScoreBar';
import {RecordAuditTaskCard} from './RecordAuditTaskCard';
import {STRIPE_WIDTH} from './STRIPE_WIDTH';
import {TASK_CARD_CONTENT_PADDING} from './TASK_CARD_CONTENT_PADDING';

interface RecordAuditFormProps {
  audit: RecordAuditResponseBody;
  tasks: ReadonlyArray<RecordAuditTask>;
  isDisabled?: boolean;
  activeFilter: RecordAuditScoreFilter;
}

const matchesFilter = (task: RecordAuditTask, filter: RecordAuditScoreFilter) =>
  match(filter)
    .with('all', () => true)
    .with('notFilled', () => isNil(task.value))
    .otherwise((value) => task.value === value);

export function RecordAuditForm(props: RecordAuditFormProps) {
  const {audit, tasks, isDisabled} = props;

  const sortedSections = [...audit.definitionSnapshot.sections].sort(
    (a, b) => a.position - b.position
  );
  const sectionIds = sortedSections.map((section) => section.originalSectionId);
  const unsectionedTasks = tasks.filter((task) => isUnsectioned(task, sectionIds));

  // Every section hides itself when the filter excludes its tasks, so an active filter
  // matching nothing would leave a blank panel — show an explicit empty state instead.
  const hasVisibleTasks = tasks.some((task) => matchesFilter(task, props.activeFilter));

  if (!hasVisibleTasks) {
    return (
      <EmptyStatus
        variant="table"
        headline={i18n.t('page.recordAudit.detail.noTasksMatchFilter')}
        data-testid="recordAuditDetail-checklist-empty"
      />
    );
  }

  return (
    <VStack spacing={6}>
      {/* Unsectioned tasks lead the checklist, ahead of the named sections. */}
      <Show when={unsectionedTasks.length > 0}>
        <SectionBlock
          auditId={audit.id}
          title={i18n.t('page.recordAudit.detail.unsectioned')}
          tasks={unsectionedTasks}
          isReadonly={isDisabled}
          activeFilter={props.activeFilter}
        />
      </Show>
      {sortedSections.map((section, index) => {
        const sectionTasks = tasks
          .filter((task) => task.sectionId === section.originalSectionId)
          .sort((a, b) => a.position - b.position);
        return (
          <SectionBlock
            key={section.originalSectionId}
            auditId={audit.id}
            number={index + 1}
            title={section.name}
            tasks={sectionTasks}
            isReadonly={isDisabled}
            activeFilter={props.activeFilter}
          />
        );
      })}
    </VStack>
  );
}

const isUnsectioned = (task: RecordAuditTask, sectionIds: string[]) => {
  if (isNil(task.sectionId)) {
    return true;
  }
  return !sectionIds.includes(task.sectionId);
};

interface SectionBlockProps {
  auditId: string;
  number?: number;
  title: string;
  tasks: ReadonlyArray<RecordAuditTask>;
  isReadonly?: boolean;
  activeFilter: RecordAuditScoreFilter;
}

const PROGRESS_BAR_WIDTH = 25;

function SectionBlock(props: SectionBlockProps) {
  // Hide the whole section when the active filter excludes all of its tasks.
  if (
    props.tasks.length === 0 ||
    !props.tasks.some((task) => matchesFilter(task, props.activeFilter))
  ) {
    return null;
  }
  // Progress reflects ALL tasks in the section, not just the filtered view.
  const filled = props.tasks.filter((task) => isNotNil(task.value)).length;
  const total = props.tasks.length;
  const ok = props.tasks.filter((task) => task.value === 'OK').length;
  const bad = props.tasks.filter((task) => task.value === 'BAD').length;
  const na = props.tasks.filter((task) => task.value === 'NA').length;

  return (
    <VStack spacing={3}>
      <VStack spacing={2}>
        {/* Mirror the task card's left chrome with spacer containers (stripe + content
            inset) so the section number lines up with the task numbers structurally.
            On mobile the inset collapses to 0 so the section heading sits flush left
            with the checklist heading above it. */}
        <HStack spacing={0} align="center">
          <Box width={[0, STRIPE_WIDTH]} />
          <Box width={[0, TASK_CARD_CONTENT_PADDING]} />
          <Box flex={1} minWidth={0}>
            {/* Wrap so the progress cluster drops below the section title on narrow widths
              instead of colliding with a long section name. */}
            <HStack justify="space-between" align="center" spacing={3} wrap>
              <HStack spacing={3} align="center">
                <Show when={isNotNil(props.number)}>
                  <Box
                    paddingHorizontal={2}
                    borderRadius="small"
                    backgroundColor="palettes.neutral.20.100"
                  >
                    <Text size="base" color="tertiary" alternative>
                      {props.number}
                    </Text>
                  </Box>
                </Show>
                <Heading size={3} alternative>
                  {props.title}
                </Heading>
              </HStack>
              <HStack spacing={3} align="center">
                <HStack spacing={1} align="center">
                  <Text size="xSmall" color="tertiary">
                    {`${i18n.t('page.recordAudit.detail.fillProgress')}:`}
                  </Text>
                  <Text size="xSmall" color="tertiary" alternative>
                    {`${filled} / ${total}`}
                  </Text>
                </HStack>
                <Box width={PROGRESS_BAR_WIDTH}>
                  <RecordAuditProgressSegments ok={ok} bad={bad} na={na} total={total} />
                </Box>
              </HStack>
            </HStack>
          </Box>
        </HStack>
        <Separator spacing={0} />
      </VStack>
      <VStack spacing={2}>
        {props.tasks.map((task, index) => (
          <Show key={task.originalTaskDefinitionId} when={matchesFilter(task, props.activeFilter)}>
            <RecordAuditTaskCard
              auditId={props.auditId}
              task={task}
              number={isNotNil(props.number) ? `${props.number}.${index + 1}` : `${index + 1}`}
              isReadonly={props.isReadonly}
            />
          </Show>
        ))}
      </VStack>
    </VStack>
  );
}
